import { ResourceQuota } from 'kubernetes-types/core/v1';
import { useMemo, useState } from 'react';

import { Button } from '@@/buttons';
import { Gauge } from '@@/Charts/Gauge';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { EffectiveQuotaLimit } from '../../types';

import { resourceQuotaDefinition } from './resourceQuotaDefinitions';
import { usageRatio } from './quantity';

/** Dials shown before the rest go behind the "more" card. */
const VISIBLE_DIALS = 6;

/** One shape for every card on the row, dials and controls alike. */
const CARD_CLASS =
  'flex h-auto min-w-[8rem] flex-col items-center gap-1 rounded border px-4 py-3';

const NEARLY_FULL = 0.9;
const FILLING_UP = 0.75;

type Dial = {
  resource: string;
  limit: EffectiveQuotaLimit;
  ratio: number;
  quotaCount: number;
};

type Props = {
  ceiling: Record<string, EffectiveQuotaLimit>;
  resourceQuotas: Array<ResourceQuota>;
};

/**
 * The limit binding each resource across all of a namespace's quotas.
 *
 * Kubernetes enforces every quota at once, so a workload is held to the
 * smallest hard value among them. That figure is what stops a pod scheduling,
 * and it may come from a quota Portainer does not manage.
 *
 * A namespace can constrain more resources than fit on a row, so the fullest
 * come first and the rest wait behind the more card.
 */
export function EffectiveCeilingDials({ ceiling, resourceQuotas }: Props) {
  const [showAll, setShowAll] = useState(false);
  const dials = useMemo(
    () => toDials(ceiling, resourceQuotas),
    [ceiling, resourceQuotas]
  );

  if (dials.length === 0) {
    return null;
  }

  const visible = showAll ? dials : dials.slice(0, VISIBLE_DIALS);
  const hidden = dials.length - visible.length;

  return (
    <div className="mb-4">
      <div className="text-muted mb-2 text-xs">
        Every quota is enforced at once, so where several limit the same
        resource the smallest one binds. Hover a dial for what it limits.
      </div>
      <div className="flex flex-wrap gap-2">
        {visible.map((dial) => (
          <DialCard key={dial.resource} dial={dial} />
        ))}
        {hidden > 0 && (
          <MoreCard
            label={`+${hidden} more`}
            hint="further from full"
            onClick={() => setShowAll(true)}
            data-cy="show-all-effective-ceiling"
          />
        )}
        {showAll && dials.length > VISIBLE_DIALS && (
          <MoreCard
            label="Show fewer"
            hint={`only the fullest ${VISIBLE_DIALS}`}
            onClick={() => setShowAll(false)}
            data-cy="show-fewer-effective-ceiling"
          />
        )}
      </div>
    </div>
  );
}

/** Both the expand and the collapse control, so they cannot drift apart. */
function MoreCard({
  label,
  hint,
  onClick,
  'data-cy': dataCy,
}: {
  label: string;
  hint: string;
  onClick: () => void;
  'data-cy': string;
}) {
  return (
    <Button
      color="default"
      onClick={onClick}
      className={`!ml-0 ${CARD_CLASS} justify-center`}
      data-cy={dataCy}
    >
      <span className="text-sm font-medium">{label}</span>
      <span className="text-muted text-xs font-normal">{hint}</span>
    </Button>
  );
}

function DialCard({ dial }: { dial: Dial }) {
  const { resource, limit, ratio, quotaCount } = dial;

  return (
    <TooltipWithChildren
      heading={resource}
      position="bottom"
      message={<DialExplanation dial={dial} />}
    >
      <div className={CARD_CLASS}>
        <Gauge
          ratio={ratio}
          className={fillColorClass(ratio)}
          aria-label={`${resource} ${Math.round(ratio * 100)}% full`}
        />
        <span className="text-center text-xs font-medium">{resource}</span>
        <span className="text-muted text-center text-xs">
          {limit.Used} of {limit.Hard}
        </span>
        {quotaCount > 1 && (
          <span className="text-muted text-center text-xs">
            tightest of {quotaCount}
          </span>
        )}
      </div>
    </TooltipWithChildren>
  );
}

function DialExplanation({ dial }: { dial: Dial }) {
  const definition = resourceQuotaDefinition(dial.resource);

  return (
    <div className="flex flex-col gap-2">
      {definition && <span>{definition}</span>}
      <span>
        Using {dial.limit.Used} of {dial.limit.Hard}, set by the quota{' '}
        {dial.limit.BoundBy}.
      </span>
      {dial.quotaCount > 1 && (
        <span>
          {dial.quotaCount} quotas limit this resource; the smallest is the one
          that binds.
        </span>
      )}
    </div>
  );
}

function fillColorClass(ratio: number) {
  if (ratio >= NEARLY_FULL) {
    return 'text-error-7 th-dark:text-error-5 th-highcontrast:text-error-5';
  }

  if (ratio >= FILLING_UP) {
    return 'text-warning-7 th-dark:text-warning-5 th-highcontrast:text-warning-5';
  }

  return 'text-success-7 th-dark:text-success-5 th-highcontrast:text-success-5';
}

/** Fullest first, so what is about to run out is what a reader sees. */
function toDials(
  ceiling: Record<string, EffectiveQuotaLimit>,
  resourceQuotas: Array<ResourceQuota>
): Array<Dial> {
  return Object.keys(ceiling)
    .map((resource) => ({
      resource,
      limit: ceiling[resource],
      ratio: usageRatio(ceiling[resource].Used, ceiling[resource].Hard),
      quotaCount: resourceQuotas.filter(
        (quota) => quota.status?.hard?.[resource] !== undefined
      ).length,
    }))
    .sort((a, b) => b.ratio - a.ratio || a.resource.localeCompare(b.resource));
}
